import { useRef, useEffect, useCallback, useState, useContext } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import MxInputField from "@rf-l1/form/text-fields/MxInputField";
import { IconSearch } from "@tabler/icons-react";
import MxIcons from "@rf-l1/MxIcons";
import { useFormContext } from "react-hook-form";
import { HPrepareReturnData } from "@rf-l1/map/helpers/HPrepareReturnData";
import { useMxMapLayoutContext } from "../context/MxMapLayout";

interface Props {}

// Autocomplete de Google Places que escribe en los campos del contexto MxMapLayout
export const MxMapSearchAutocomplete = ({}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary("places");
  const [inputValue, setInputValue] = useState("");
  const { setValue, trigger } = useFormContext();
  const { fieldsNames } = useMxMapLayoutContext();
  const map = useMap("map");
  const onPlaceSelect = useCallback(
    (place: any) => {
      setInputValue(place?.formatted_address || "");
      const parsed = HPrepareReturnData(place);

      if (fieldsNames?.lat) {
        setValue(fieldsNames.lat, parsed.lat, {
          shouldTouch: true,
          shouldDirty: true
        });
        trigger(fieldsNames.lat);
      }
      if (fieldsNames?.lng) {
        setValue(fieldsNames.lng, parsed.lng, {
          shouldTouch: true,
          shouldDirty: true
        });
        trigger(fieldsNames.lng);
      }
      // if (fieldsNames?.address) {
      //   setValue(
      //     fieldsNames.address,
      //     parsed.address || place?.formatted_address || "",
      //     {
      //       shouldTouch: true,
      //       shouldDirty: true
      //     }
      //   );
      //   trigger(fieldsNames.address);
      // }
      if (fieldsNames?.direction) {
        setValue(
          fieldsNames.direction,
          place?.formatted_address || parsed.address || "",
          {
            shouldTouch: true,
            shouldDirty: true
          }
        );
        trigger(fieldsNames.direction);
      }
      setTimeout(() => {
        map?.setZoom(15);
      }, 500);
    },
    [fieldsNames, setValue, map]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleFocus = () => {
    inputRef.current?.setAttribute("value", inputValue);
  };

  const initializeAutocomplete = useCallback(() => {
    if (!places || !inputRef.current) return;

    const autocomplete = new places.Autocomplete(inputRef.current, {
      // Permitimos direcciones y lugares (sin restringir solo a "geocode")
      componentRestrictions: { country: "do" },
      // Sesgo por el área visible del mapa si existe
      bounds: (map as any)?.getBounds?.(),
      strictBounds: false,
      fields: [
        "geometry",
        "name",
        "address_components",
        "formatted_address",
        "opening_hours",
        "business_status",
        "place_id",
        "types"
      ]
    });

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      onPlaceSelect(place);
      // Centrar el mapa en el resultado seleccionado
      try {
        const parsed = HPrepareReturnData(place as any);
        if (parsed?.lat && parsed?.lng) {
          map?.panTo({ lat: parsed.lat, lng: parsed.lng });
        }
      } catch {}
    });
  }, [places, onPlaceSelect, map]);

  useEffect(() => {
    initializeAutocomplete();
  }, [initializeAutocomplete]);

  return (
    <MxInputField
      starticon={
        <MxIcons color="primary.main" children={<IconSearch stroke={2.5} />} />
      }
      inputProps={{
        id: "mapSearch",
        placeholder: "Buscar lugar, dirección o establecimiento",
        value: inputValue,
        onChange: handleInputChange,
        onFocus: handleFocus
      }}
      inputRef={inputRef}
    />
  );
};
